import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
// Constants
import { COOKIE_TIMER, CookiePolicyName } from './utils/Constants';
import {
  NEWSLETTER_ADMIN_PAGE_URL,
  NEWSLETTER_CONFIRM_EMAIL_PAGE_URL,
  NEWSLETTER_PAGE_URL,
  NEWSLETTER_UNSUBSCRIBE_PAGE_URL,
  BOOKING_PAGE_URL,
  BOOKING_ADMIN_PAGE_URL,
  EXTRA_LINKS_URL,
  FAQS_PAGE_URL,
  GALLERY_PAGE_URL,
  HOME_PAGE_URL,
  LOGIN_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  ONLINE_SESSIONS_URL,
  POLICIES_PAGE_URL,
  RATES_AND_TRIBUTES_URL,
  REAL_TIME_URL,
  RESET_PASS_PAGE_URL,
  ABOUT_PAGE_URL,
  CONTACT_PAGE_URL,
} from './utils/Routes';
// Components
import LoadingScreen from './components/utils/LoadingScreen';
// Utils
import { AuthenticateAdmin } from './utils/user/AuthenticateUser';
// Normal import for HomePage (no lazy loading)
import HomePage from './pages/home/HomePage';
import LinksPage from './pages/links/LinksPage';
import GalleryPage from './pages/gallery/GalleryPage';
import FaqPage from './pages/faq/FaqPage';
import AboutPage from './pages/about/AboutPage';
import OnlinePage from './pages/online/OnlinePage';
import RealTimePage from './pages/realTime/RealTimePage';
import RatesPage from './pages/rates/RatesPage';
import LoginPage from './users/login/LoginPage';
import ForgettenPasswordPage from './pages/user/ForgettenPasswordPage';
// Booking
import BookingPage from './pages/booking/BookingPage';
import BookingAdminPage from './pages/booking/admin/BookingAdminPage';
import ConfirmBookingAutoPage from './pages/booking/admin/ConfirmBookingAutoPage';
import DenyBookingAutoPage from './pages/booking/admin/DenyBookingAutoPage';
// Newsletter
import NewsletterSignUpPage from './pages/newsletter/NewsletterSignUpPage';
import NewsletterAdminPage from './pages/newsletter/admin/NewsletterAdminPage';
import NewsletterValidationPage from './pages/newsletter/NewsletterValidationPage';
import NewsletterUnsubscribePage from './pages/newsletter/NewsletterUnsubscribePage';

// Lazy-loaded Pages
const MaintenancePage = lazy(() =>
  import('./pages/maintenance/MaintenancePage')
);
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));
const Error404 = lazy(() => import('./pages/error/Error404'));
const TermAndPoliciesPage = lazy(() =>
  import('./pages/policies/TermAndPoliciesPage')
);

// Components
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreedToCookies, setHasAgreedToCookies] = useState(true);

  useEffect(() => {
    const cookie = localStorage.getItem(CookiePolicyName);

    if (cookie) {
      setHasAgreedToCookies(true);
    } else {
      setHasAgreedToCookies(false);
    }
  }, []);

  useEffect(() => {
    if (hasAgreedToCookies) {
      setIsVisible(false);
    }

    const timer = setTimeout(() => {
      if (!hasAgreedToCookies) {
        setIsVisible(true);
      }
    }, COOKIE_TIMER);

    return () => clearTimeout(timer);
  }, [hasAgreedToCookies]);

  return (
    <>
      {isVisible && (
        <Suspense>
          <CookieConsentModal setHasAgreedToCookies={setHasAgreedToCookies} />
        </Suspense>
      )}

      {/* Suspense component wraps the lazy-loaded components */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Main page routes */}
          <Route path={HOME_PAGE_URL} index element={<HomePage />} />

          {/* Eager loaded */}
          <Route path={ABOUT_PAGE_URL} element={<AboutPage />} />
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />
          <Route path={GALLERY_PAGE_URL} element={<GalleryPage />} />
          <Route path={EXTRA_LINKS_URL} element={<LinksPage />} />
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />
          <Route path={FAQS_PAGE_URL} element={<FaqPage />} />
          <Route path={ONLINE_SESSIONS_URL} element={<OnlinePage />} />
          <Route path={REAL_TIME_URL} element={<RealTimePage />} />
          <Route path={RATES_AND_TRIBUTES_URL} element={<RatesPage />} />

          {/* User routes */}
          <Route path={LOGIN_PAGE_URL} element={<LoginPage />} />
          <Route
            path={RESET_PASS_PAGE_URL}
            element={<ForgettenPasswordPage />}
          />

          {/* Booking routes */}
          <Route path={BOOKING_PAGE_URL} element={<BookingPage />} />

          {/* Other */}
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />

          {/* Newsletter routes */}
          <Route
            path={NEWSLETTER_PAGE_URL}
            element={<NewsletterSignUpPage />}
          />
          <Route
            path={NEWSLETTER_ADMIN_PAGE_URL}
            element={
              <AuthenticateAdmin>
                <NewsletterAdminPage />
              </AuthenticateAdmin>
            }
          />
          <Route
            path={`${NEWSLETTER_CONFIRM_EMAIL_PAGE_URL}/:userId/:uniqueString/:verificationTokenId`}
            element={<NewsletterValidationPage />}
          />
          <Route
            path={`${NEWSLETTER_UNSUBSCRIBE_PAGE_URL}/:subId/:uniqueString`}
            element={<NewsletterUnsubscribePage />}
          />

          <Route
            path={BOOKING_ADMIN_PAGE_URL}
            element={
              <AuthenticateAdmin>
                <BookingAdminPage />
              </AuthenticateAdmin>
            }
          />
          <Route
            path={`/bookings/confirm-booking/:uniqueString/:bookingId`}
            element={<ConfirmBookingAutoPage />}
          />
          <Route
            path={`/bookings/deny-booking/:uniqueString/:bookingId`}
            element={<DenyBookingAutoPage />}
          />

          {/* Error routes */}
          <Route path='*' element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
